/** Pickable ESG monthly dashboard widgets (flat `GET .../monthly` payload). */
export enum EsgMonthlyMetricType {
  TOTAL_WASTE_DIVERTED = 'total_waste_diverted_kg',
  WASTE_DIVERSION_RATE = 'waste_diversion_rate_percent',
  /** Avoided emissions (tCO2e) — reported outside the GHG inventory. */
  TOTAL_CO2 = 'esg_total_co2',
  RECYCLED_WASTE_BREAKDOWN = 'recycled_waste_breakdown_by_type',
  CARBON_AVOIDED_BY_STREAM = 'avoided_tco2e_by_stream',
  CARBON_AVOIDED_BY_MATERIAL = 'avoided_tco2e_by_material',
  FILL_LEVEL_REPORT_COUNT = 'fill_level_report_count',
  TRAINING_HOURS_TOTAL = 'training_hours_total',
  /** Health & safety section (GRI 403-9): rendered as its own block, not a card. */
  HEALTH_SAFETY = 'esg_health_safety',
  WATER_USE = 'esg_water_use',
  ELECTRICITY_USE = 'esg_electricity_use',
  OIL_USE = 'esg_oil_use',
  GAS_USE = 'esg_gas_use',
}

export const ESG_MONTHLY_METRIC_LABELS: Record<EsgMonthlyMetricType, string> = {
  [EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED]: 'Waste collected via platform',
  [EsgMonthlyMetricType.WASTE_DIVERSION_RATE]: 'Pickup completion rate (by mass)',
  [EsgMonthlyMetricType.TOTAL_CO2]: 'Avoided emissions',
  [EsgMonthlyMetricType.RECYCLED_WASTE_BREAKDOWN]: 'Waste composition by type & material',
  [EsgMonthlyMetricType.CARBON_AVOIDED_BY_STREAM]: 'Avoided emissions by material',
  [EsgMonthlyMetricType.CARBON_AVOIDED_BY_MATERIAL]: 'Avoided emissions by category',
  [EsgMonthlyMetricType.FILL_LEVEL_REPORT_COUNT]: 'Fill-level reports',
  [EsgMonthlyMetricType.TRAINING_HOURS_TOTAL]: 'Employee training hours',
  [EsgMonthlyMetricType.HEALTH_SAFETY]: 'Health & safety',
  [EsgMonthlyMetricType.WATER_USE]: 'Water',
  [EsgMonthlyMetricType.ELECTRICITY_USE]: 'Electricity',
  [EsgMonthlyMetricType.OIL_USE]: 'Oil',
  [EsgMonthlyMetricType.GAS_USE]: 'Gas',
};

/** Customize dialog list order (matches Monthly number-card order where applicable). */
export const PICKABLE_ESG_MONTHLY_METRICS: EsgMonthlyMetricType[] = [
  EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED,
  EsgMonthlyMetricType.WASTE_DIVERSION_RATE,
  EsgMonthlyMetricType.TOTAL_CO2,
  EsgMonthlyMetricType.FILL_LEVEL_REPORT_COUNT,
  EsgMonthlyMetricType.TRAINING_HOURS_TOTAL,
  EsgMonthlyMetricType.HEALTH_SAFETY,
  EsgMonthlyMetricType.WATER_USE,
  EsgMonthlyMetricType.ELECTRICITY_USE,
  EsgMonthlyMetricType.OIL_USE,
  EsgMonthlyMetricType.GAS_USE,
  EsgMonthlyMetricType.RECYCLED_WASTE_BREAKDOWN,
  EsgMonthlyMetricType.CARBON_AVOIDED_BY_STREAM,
];

/** Non-chart ESG metrics: summary block (Monthly tab, before utilities). */
export const ESG_MONTHLY_SUMMARY_METRIC_ORDER: EsgMonthlyMetricType[] = [
  EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED,
  EsgMonthlyMetricType.WASTE_DIVERSION_RATE,
  EsgMonthlyMetricType.TOTAL_CO2,
  EsgMonthlyMetricType.FILL_LEVEL_REPORT_COUNT,
  EsgMonthlyMetricType.TRAINING_HOURS_TOTAL,
];

/** Non-chart ESG metrics: utilities (Monthly tab, after Avg Price). */
export const ESG_MONTHLY_UTILITY_METRIC_ORDER: EsgMonthlyMetricType[] = [
  EsgMonthlyMetricType.WATER_USE,
  EsgMonthlyMetricType.ELECTRICITY_USE,
  EsgMonthlyMetricType.OIL_USE,
  EsgMonthlyMetricType.GAS_USE,
];

export const DEFAULT_ESG_MONTHLY_METRICS: EsgMonthlyMetricType[] = [
  ...PICKABLE_ESG_MONTHLY_METRICS,
];

export function isEsgMonthlyChartMetric(metric: EsgMonthlyMetricType): boolean {
  return (
    metric === EsgMonthlyMetricType.RECYCLED_WASTE_BREAKDOWN ||
    metric === EsgMonthlyMetricType.CARBON_AVOIDED_BY_STREAM ||
    metric === EsgMonthlyMetricType.CARBON_AVOIDED_BY_MATERIAL
  );
}
